import { api_fetch, api_fetch_throws } from "./client"

/** How `rate` is read: percent of invoice cost / shorted value, or dollars per SKU / flat. */
export type ContractTermBasis = "pct_of_invoice_cost" | "pct_of_shorted_value" | "flat_per_sku" | "flat"

/** `suggested` rows come from AI extraction; a user confirms them. */
export type ContractTermStatus = "suggested" | "confirmed"

/** A rate the brand signed with a distributor or retailer (`contract_term` table). */
export interface ContractTerm {
  id: string
  org_id: string
  created_at: string
  updated_at: string
  contract_document_id: string | null
  customer_id: string | null
  retailer_name: string | null
  distributor: string | null
  program: string
  match_pattern: string | null
  basis: ContractTermBasis
  /** Percent for pct bases (1.17 = 1.17%), dollars for flat bases. */
  rate: number | null
  minimum_amount: number | null
  threshold_pct: number | null
  threshold_window_weeks: number | null
  effective_from: string | null
  effective_to: string | null
  status: ContractTermStatus
  source_excerpt: string | null
  notes: string | null
}

/** Editable fields; `null` clears a field on PATCH, an absent key leaves it alone. */
export type ContractTermInput = Partial<Omit<ContractTerm, "id" | "org_id" | "created_at" | "updated_at">>

/** A percentage charge measured against the signed rate (server `RateCheck`). */
export interface RateCheck {
  contracted_rate: number
  charged_rate: number
  charged_total: number
  basis_total: number
  expected_total: number
  /** charged_total - expected_total; negative when undercharged. */
  overcharge: number
  within_tolerance: boolean
  line_count: number
}

export interface ProgramRateCheck extends RateCheck {
  program_label: string
  term_id: string
  term_program: string
  term_status: ContractTermStatus
  effective_from: string | null
  effective_to: string | null
  source_excerpt: string | null
}

/** Charged program rates on a deduction vs the org's contract terms (deduction detail). */
export interface ContractRateCheck {
  programs: ProgramRateCheck[]
  total_overcharge: number
}

export interface ListContractTermsParams {
  contract_document_id?: string
  status?: ContractTermStatus
  distributor?: string
}

export function contract_terms_key(params: ListContractTermsParams): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value) qs.set(key, value)
  }
  const query = qs.toString()
  return query ? `/contract-term?${query}` : "/contract-term"
}

export async function list_contract_terms(params: ListContractTermsParams): Promise<ContractTerm[]> {
  return api_fetch_throws<ContractTerm[]>("/contract-term", { params: { ...params } })
}

export async function create_contract_term(input: ContractTermInput): Promise<ContractTerm> {
  return api_fetch_throws<ContractTerm>("/contract-term", { method: "POST", body: input })
}

export async function update_contract_term(id: string, patch: ContractTermInput): Promise<ContractTerm> {
  return api_fetch_throws<ContractTerm>(`/contract-term/${id}`, { method: "PATCH", body: patch })
}

export async function delete_contract_term(id: string): Promise<void> {
  const res = await api_fetch<void>(`/contract-term/${id}`, { method: "DELETE" })
  if (!res.ok) {
    throw res.error
  }
}
